"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { TriangleAlert as AlertTriangle, ArrowRight, Droplets, Flame, Gauge, PhoneCall, Pipette, ShieldAlert, Waves, Wrench } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
interface PlumbingIssueItem {
  id: string;
  title: string;
  tag: string;
  description: string;
  urgency: "Immediate" | "Same Day" | "Urgent";
  link: string;
  icon: React.ElementType;
}
const plumbingIssuesList: PlumbingIssueItem[] = [{
  id: "clogged-drains",
  title: "Clogged Drains",
  tag: "Drainage",
  description: "Slow sink drains, gurgling kitchen pipes, and standing shower water caused by grease, hair, and buildup.",
  urgency: "Same Day",
  link: "/drain-problems/",
  icon: Waves
}, {
  id: "water-leaks",
  title: "Water Leaks",
  tag: "Hidden Damage",
  description: "Dripping fixtures, damp drywall, slab leaks, and unexplained spikes on your monthly water bill.",
  urgency: "Immediate",
  link: "/water-leak-problems/",
  icon: Droplets
}, {
  id: "no-hot-water",
  title: "No Hot Water",
  tag: "Water Heater",
  description: "Faulty heating elements, leaking tank basins, pilot light failures, or undersized water heater units.",
  urgency: "Same Day",
  link: "/water-heater-problems/",
  icon: Flame
}, {
  id: "toilet-problems",
  title: "Toilet Problems",
  tag: "Sanitation",
  description: "Constantly running water, weak flushes, persistent bowl clogs, and base seal water leakage.",
  urgency: "Urgent",
  link: "/toilet-problems/",
  icon: Pipette
}, {
  id: "low-water-pressure",
  title: "Low Water Pressure",
  tag: "Supply Flow",
  description: "Weak flow in showers and faucets caused by sediment in aerators, hidden pipe leaks, or faulty pressure valves.",
  urgency: "Same Day",
  link: "/water-pressure-problems/",
  icon: Gauge
}, {
  id: "sewer-backup",
  title: "Sewer Line Backup",
  tag: "Hazard",
  description: "Multiple slow drains, foul odor in living areas, and waste backing up into tubs or lowest drains.",
  urgency: "Immediate",
  link: "/sewer-line-repair/",
  icon: AlertTriangle
}, {
  id: "burst-pipes",
  title: "Burst & Frozen Pipes",
  tag: "Emergency",
  description: "Sudden pipe fractures causing active flooding, high water volume loss, and severe structural risk.",
  urgency: "Immediate",
  link: "/pipe-problems/",
  icon: ShieldAlert
}, {
  id: "fixture-damage",
  title: "Fixture Damage",
  tag: "Repairs",
  description: "Cracked sinks, loose handles, malfunctioning garbage disposals, and worn shutoff valves.",
  urgency: "Same Day",
  link: "/fixture-problems/",
  icon: Wrench
}];
export default function PlumbingIssues() {
  const shouldReduceMotion = useReducedMotion();
  return <section id="commonplumbingissues" data-nav="light" className="bg-background text-foreground py-16 md:py-24">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-3xl text-center">
          <Badge variant="secondary" className="mb-3 font-medium text-xs tracking-wider uppercase">
            Jonesboro Diagnostic Guide
          </Badge>
          <h2 className="text-foreground text-3xl font-bold tracking-tight sm:text-4xl">
            Common Plumbing Issues We Fix Fast
          </h2>
          <p className="text-muted-foreground mt-4 text-base sm:text-lg">
            Plumbing trouble disrupts your home and can cause costly damage.
            Identify your issue below and get dependable 24/7 emergency service
            anywhere in Jonesboro, GA.
          </p>
        </div>

        <div className="mt-12 grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-4">
          {plumbingIssuesList.map((issue, issueIdx) => {
          const IconComponent = issue.icon;
          return <motion.div key={issue.id} data-index={issueIdx} initial={shouldReduceMotion ? {
            opacity: 1
          } : {
            opacity: 0,
            y: 16
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true,
            margin: "-40px"
          }} transition={{
            duration: 0.4,
            delay: shouldReduceMotion ? 0 : issueIdx * 0.05,
            ease: "easeOut"
          }} className="h-full">
                <Card className="border-border bg-card shadow-sm hover:shadow-md transition-[box-shadow,border-color] h-full flex flex-col justify-between">
                  <CardHeader className="space-y-3 pb-3">
                    <div className="flex items-center justify-between gap-2">
                      <div className="bg-secondary text-secondary-foreground flex size-11 items-center justify-center rounded-lg">
                        <IconComponent className="size-5 text-primary" />
                      </div>
                      <Badge variant={issue.urgency === "Immediate" ? "destructive" : issue.urgency === "Urgent" ? "default" : "outline"} className="text-[11px]">
                        {issue.urgency}
                      </Badge>
                    </div>
                    <div>
                      <span className="text-muted-foreground text-xs font-semibold uppercase tracking-wider">
                        {issue.tag}
                      </span>
                      <CardTitle className="text-card-foreground mt-1 text-lg font-semibold">
                        {issue.title}
                      </CardTitle>
                    </div>
                  </CardHeader>
                  <CardContent className="flex-1">
                    <CardDescription className="text-muted-foreground text-sm leading-relaxed">
                      {issue.description}
                    </CardDescription>
                  </CardContent>
                  <CardFooter className="pt-2">
                    <Button asChild variant="ghost" size="sm" className="w-full justify-between px-0 font-medium text-primary hover:text-primary/80">
                      <Link to={issue.link}>
                        <span>Diagnose & Fix</span>
                        <ArrowRight className="size-4 ml-1" />
                      </Link>
                    </Button>
                  </CardFooter>
                </Card>
              </motion.div>;
        })}
        </div>

        <div className="mt-12 rounded-xl border border-border bg-secondary/50 p-6 sm:p-8">
          <div className="flex flex-col items-center justify-between gap-6 text-center md:flex-row md:text-left">
            <div className="max-w-2xl">
              <h3 className="text-foreground text-xl font-bold">
                Facing an unlisted plumbing emergency?
              </h3>
              <p className="text-muted-foreground mt-1 text-sm sm:text-base">
                Our licensed technicians are on call 24 hours a day to isolate leaks,
                clear critical lines, and restore safe water flow in Jonesboro.
              </p>
            </div>
            <div className="flex flex-wrap items-center justify-center gap-3 shrink-0">
              <Button asChild size="lg" className="shadow-sm">
                <Link to="/emergency-plumbing/">
                  <ShieldAlert className="size-4" />
                  <span>24/7 Emergency Help</span>
                </Link>
              </Button>
              <Button asChild variant="outline" size="lg">
                <Link to="/contact/">
                  <PhoneCall className="size-4" />
                  <span>Contact Our Team</span>
                </Link>
              </Button>
            </div>
          </div>
        </div>
      </div>
    </section>;
}